import React, { Fragment, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { getUrlToLoginPage, sendToLogoutPage } from './withAuth';
import { useSelector } from 'react-redux';
import {
    getSessionInfo,
    selectUserAuthState,
    UserAuthState,
} from 'common/features/store/duck/account/duck';
import { SessionExpiredModal } from './SessionExpiredModal';
import { useBrowserTabFocused } from 'hooks/useBrowserTabFocused';
import { selectHomeDetailsProfileEmail } from 'common/features/store/duck/home/duck';
import { useLocation } from 'react-router';
import { Routes } from 'routes';
import { RunNewCoordinatorDashboardWelcomeMessageLogic } from 'utils';
import { Branding } from 'common/env';

// the amount of seconds we want to wait between polling requests.
export const SESSION_EXPIRED_CHECK_INTERVAL = new Number(
    process.env.REACT_APP_SESSION_EXPIRED_CHECK_INTERVAL || 5 * 60
) as number;

const GO_TO_SHOP = 'rr_go_to_shop';
const SESSION_EXPIRED_BROADCAST = 'rr_session_expired';
const BROADCAST_CHANNEL_IDENTIFIER = Branding;

export const useBroadcastChannel = () => {
    // eslint-disable-next-line compat/compat
    const broadcastChannel = useRef(new BroadcastChannel(BROADCAST_CHANNEL_IDENTIFIER));

    useEffect(() => {
        broadcastChannel.current.onmessage = (ev) => {
            if (ev.data === GO_TO_SHOP) {
                goToShop();
            }
        };
    }, []);

    return broadcastChannel.current;
};

// Generates a unique id to identify the browser tab
export const useBrowserTabId = () => {
    const tabIdKey = `${Branding}_TabId`;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const browserTabId = (window as any)[tabIdKey] as number;

    if (!browserTabId) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any)[tabIdKey] = Math.trunc(Math.random() * 1000000);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (window as any)[tabIdKey];
};

const ignoredRoutes = [Routes.StartProgram, Routes.Enroll, Routes.EnrollV2, Routes.EnrollFamily];

const goToShop = () => {
    // hard redirects to shop page (refreshes the entire page)
    window.location.pathname = Routes.Shop;
};

export const LoginWhenSessionExpires = ({ children }: { children: ReactNode }) => {
    const browserTabId = useBrowserTabId();
    const userAuthState = useSelector(selectUserAuthState);
    const redirectFlag = useRef(false);
    // Set by forced re-auth signals; only a successful re-login clears it.
    const forcedReauth = useRef(false);
    const [sessionExpiredIsOpen, setSessionExpiredIsOpen] = useState(false);
    const broadcastChannel = useBroadcastChannel();
    const browserTabFocused = useBrowserTabFocused();

    const location = useLocation();

    const ignoreCurrentRoute = useMemo(
        () =>
            ignoredRoutes.some((route) =>
                location.pathname.toLowerCase().startsWith(route.toLowerCase())
            ),
        [location.pathname]
    );

    const onSignInClicked = async () => {
        const loginUrl = getUrlToLoginPage(`/close-window?${browserTabId}`);
        window.open(loginUrl, '_blank')!;
    };

    const onSignOutClicked = () => {
        sendToLogoutPage();
    };

    const email = useSelector(selectHomeDetailsProfileEmail);

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const fn = async (e: MessageEvent<any>) => {
            const { message, payload } = e.data;
            const { tabId } = payload || {};

            if (message === 'login-success' && tabId == browserTabId) {
                const isTheSameUser = payload.email === email;

                broadcastChannel.postMessage({
                    message: 'login-success-close-window',
                });

                if (!isTheSameUser) {
                    broadcastChannel.postMessage(GO_TO_SHOP);
                    goToShop();
                }

                // closes the session expired dialog
                forcedReauth.current = false;
                setSessionExpiredIsOpen(false);
            }
        };

        broadcastChannel.addEventListener('message', fn);

        return () => broadcastChannel.removeEventListener('message', fn);
    }, [email, broadcastChannel, browserTabId]);

    // apiFetch posts a '401_signal' message whenever any API call returns 401
    // (common/api/utils/index.ts). Reacting to it here closes the gap between
    // polling cycles: without it, a call failing right after the token expires
    // surfaces as a feature outage (e.g. "Shop Online is currently unavailable")
    // instead of the re-auth prompt.
    useEffect(() => {
        if (userAuthState !== UserAuthState.AUTHENTICATED || ignoreCurrentRoute) {
            return;
        }

        let checking = false;

        const onUnauthorizedSignal = async (e: MessageEvent) => {
            // Same-window signal only; ignore messages from iframes and other origins.
            if (e.origin !== window.location.origin) {
                return;
            }
            if (e.data !== '401_signal' && e.data !== '401_signal_reauth') {
                return;
            }
            if (redirectFlag.current) {
                return;
            }

            // Forced signals open the prompt directly: their 401 already proves
            // re-auth is needed, and the plain signal's in-flight check would swallow them.
            if (e.data === '401_signal_reauth') {
                redirectFlag.current = true;
                forcedReauth.current = true;
                setSessionExpiredIsOpen(true);
                broadcastChannel.postMessage({
                    message: SESSION_EXPIRED_BROADCAST,
                    payload: { forced: true },
                });
                const { userId } = await getSessionInfo();
                RunNewCoordinatorDashboardWelcomeMessageLogic(userId);
                return;
            }

            if (checking) {
                return;
            }
            checking = true;
            try {
                // Confirm with the session endpoint so a stray 401 (e.g. a
                // permission issue on one endpoint) doesn't prompt re-auth.
                const { hasSessionExpired, userId } = await getSessionInfo();
                if (hasSessionExpired && !redirectFlag.current) {
                    redirectFlag.current = true;
                    setSessionExpiredIsOpen(true);
                    broadcastChannel.postMessage({
                        message: SESSION_EXPIRED_BROADCAST,
                        payload: { forced: false },
                    });
                    RunNewCoordinatorDashboardWelcomeMessageLogic(userId);
                }
            } finally {
                checking = false;
            }
        };

        window.addEventListener('message', onUnauthorizedSignal);
        return () => window.removeEventListener('message', onUnauthorizedSignal);
    }, [userAuthState, ignoreCurrentRoute, broadcastChannel]);

    // Cross-tab sync: a tab that detects the expiry notifies the rest so the
    // prompt is already open when the user switches tabs.
    useEffect(() => {
        if (userAuthState !== UserAuthState.AUTHENTICATED || ignoreCurrentRoute) {
            return;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const fn = (e: MessageEvent<any>) => {
            if (e.data?.message === SESSION_EXPIRED_BROADCAST && !redirectFlag.current) {
                redirectFlag.current = true;
                forcedReauth.current = !!e.data?.payload?.forced;
                setSessionExpiredIsOpen(true);
            }
        };
        broadcastChannel.addEventListener('message', fn);
        return () => broadcastChannel.removeEventListener('message', fn);
    }, [userAuthState, ignoreCurrentRoute, broadcastChannel]);

    useEffect(() => {
        if (
            !browserTabFocused ||
            userAuthState !== UserAuthState.AUTHENTICATED ||
            ignoreCurrentRoute
        ) {
            return;
        }

        redirectFlag.current = false;

        let timeoutHandle: NodeJS.Timeout;
        let canceled = false;

        const checkSessionState = async () => {
            const { hasSessionExpired, userId } = await getSessionInfo();

            // NOTE: This hook might get canceled while the above async call
            // is waiting, so we need to check here for the canceled flag.
            if (canceled) {
                return;
            }

            // forcedReauth reopens the prompt after a blur/refocus cycle.
            if (
                (hasSessionExpired || forcedReauth.current) &&
                !document.hidden &&
                !redirectFlag.current
            ) {
                redirectFlag.current = true;
                setSessionExpiredIsOpen(true);
                if (hasSessionExpired) {
                    broadcastChannel.postMessage({
                        message: SESSION_EXPIRED_BROADCAST,
                        payload: { forced: false },
                    });
                }

                // NOTE: FEC-1221 & FEC-1174
                // special case we need to run some logic for new-coordinator dashboard when session expires
                RunNewCoordinatorDashboardWelcomeMessageLogic(userId);
            }

            if (!hasSessionExpired && !forcedReauth.current) {
                redirectFlag.current = false;
                setSessionExpiredIsOpen(false);
            }

            if (!canceled) {
                timeoutHandle = setTimeout(
                    checkSessionState,
                    SESSION_EXPIRED_CHECK_INTERVAL * 1000
                );
            }
        };

        checkSessionState();

        return () => {
            canceled = true;
            clearTimeout(timeoutHandle);
            setSessionExpiredIsOpen(false);
        };
    }, [userAuthState, browserTabFocused, ignoreCurrentRoute, broadcastChannel]);

    return (
        <Fragment>
            <SessionExpiredModal
                isOpen={sessionExpiredIsOpen}
                setIsOpen={setSessionExpiredIsOpen}
                onSignInClicked={onSignInClicked}
                onSignOutClicked={onSignOutClicked}
            />
            {children}
        </Fragment>
    );
};
